// @flow

import moment from 'moment'
import {
  buildPriceLine,
  PRICE_LINE_TYPE_DISCOUNT,
  PRICE_LINE_TYPE_FEE,
  PRICE_LINE_TYPE_TOTAL,
  PRICE_LINE_TYPE_PRODUCT
} from '../types/price-line'
import { ORDER_STATUS_PAID } from './order-api-client-constants'
import type { Order } from '../types/order'
import type { OrderCreated } from '../types/order-created'
import { buildOrderCreated } from '../types/order-created'
import { buildOrderInitiatedPayment } from '../types/order-initiated-payment'
import type { OrderInitiatedPayment } from '../types/order-initiated-payment'
import type { OrderStatus } from '../types/order-status'
import { buildOrderStatus } from '../types/order-status'
import type { PaymentMethodsOrder } from '../types/payment-methods-order'
import { buildPaymentMethodsOrder } from '../types/payment-methods-order'
import { buildPaymentOption } from '../types/payment-option'
import { buildPaymentMethod } from '../types/payment-method'
import { buildOrderItemPaymentConfiguration } from '../types/order-item-payment-configuration'
import { buildPriceSpecification } from '../types/price-specification'
import { buildPaymentTermConfiguration } from '../types/payment-term-configuration'
import type { PriceSpecification } from '../types/price-specification'
import type OrderApiClientInterface, { IbanValidationResult, OrderValidationStage } from './order-api-client-interface'
import type { Price } from '../types/price'
import { buildPriceItemSpecification } from '../types/price-item-specification'
import type { PaymentSpecification } from '../types/payment-specification'
import { buildPaymentSpecification } from '../types/payment-specification'
import { buildPaymentItemConfiguration } from '../types/payment-item-configuration'
import { buildPaymentLine } from '../types/payment-line'
import type { AddressInfo } from '../types/address-info'
import type { ValidationResult } from '../types/validation-result'
import type { Subscription } from '../types/subscription'
import type { SubscriptionReminderCancellationResult } from '../types/subscription-reminder-cancellation-result'
import type { CombiDealDiscountSuggestions } from '../types/combi-deal-discount-suggestions'
import type { OrderSentResult } from '../types/order-sent-result'

const buildEuroPrice = function (amount: number): Price {
  return { amount: amount, currency: 'EUR' }
}

const buildPaymentSpecificationFromOrder = function (order: Order): PaymentSpecification {
  const paymentSpecification = buildPaymentSpecification()
  Object.keys(order.items).forEach(key => {
    const itemConfiguration = buildPaymentItemConfiguration()
    itemConfiguration.payments = [
      buildPaymentLine(
        'direct',
        moment().format('YYYY-MM-DD'),
        [
          buildPriceLine(
            PRICE_LINE_TYPE_TOTAL,
            buildEuroPrice(15),
            buildEuroPrice(10),
            buildEuroPrice(5)
          )
        ],
        buildPriceLine(
          PRICE_LINE_TYPE_TOTAL,
          buildEuroPrice(15),
          buildEuroPrice(10),
          buildEuroPrice(5)
        )
      ),
      buildPaymentLine(
        'term',
        moment().format('YYYY-MM-DD'),
        [
          buildPriceLine(
            PRICE_LINE_TYPE_TOTAL,
            buildEuroPrice(15),
            buildEuroPrice(10),
            buildEuroPrice(5)
          )
        ],
        buildPriceLine(
          PRICE_LINE_TYPE_TOTAL,
          buildEuroPrice(15),
          buildEuroPrice(10),
          buildEuroPrice(5)
        )
      )
    ]

    paymentSpecification.itemConfigurations[key] = itemConfiguration
  })

  return paymentSpecification
}

const buildPriceSpecificationFromOrder = function (order: Order): PriceSpecification {
  const priceSpecification = buildPriceSpecification()
  let index = 0
  Object.keys(order.items).forEach(key => {
    index++
    const productPriceLine = buildPriceLine(
      PRICE_LINE_TYPE_PRODUCT,
      buildEuroPrice(500 * index),
      buildEuroPrice(30 * index),
      buildEuroPrice(530 * index),
      order.items[key].id,
      '6 %'
    )
    const discountPriceLine = buildPriceLine(
      PRICE_LINE_TYPE_DISCOUNT,
      buildEuroPrice(-50 * index),
      buildEuroPrice(-3 * index),
      buildEuroPrice(-53 * index),
      PRICE_LINE_TYPE_DISCOUNT,
      '6 %'
    )
    const feePriceLine = buildPriceLine(
      PRICE_LINE_TYPE_FEE,
      buildEuroPrice(100 * index),
      buildEuroPrice(21 * index),
      buildEuroPrice(121 * index),
      PRICE_LINE_TYPE_FEE,
      '6 %'
    )
    const totalPriceLine = buildPriceLine(
      PRICE_LINE_TYPE_TOTAL,
      buildEuroPrice(550 * index),
      buildEuroPrice(48 * index),
      buildEuroPrice(598 * index),
      PRICE_LINE_TYPE_TOTAL,
      '6 %'
    )
    priceSpecification.itemSpecifications[key] = buildPriceItemSpecification([productPriceLine, discountPriceLine, feePriceLine], totalPriceLine)
  })
  const orderItemsLength = Object.keys(order.items).length
  const multiplier = (orderItemsLength * (orderItemsLength + 1)) / 2
  const productTotals = buildPriceLine(
    PRICE_LINE_TYPE_PRODUCT,
    buildEuroPrice(500 * multiplier),
    buildEuroPrice(30 * multiplier),
    buildEuroPrice(530 * multiplier)
  )
  const discountTotals = buildPriceLine(
    PRICE_LINE_TYPE_DISCOUNT,
    buildEuroPrice(-50 * multiplier),
    buildEuroPrice(-3 * multiplier),
    buildEuroPrice(-53 * multiplier)
  )
  const feeTotals = buildPriceLine(
    PRICE_LINE_TYPE_FEE,
    buildEuroPrice(100 * multiplier),
    buildEuroPrice(21 * multiplier),
    buildEuroPrice(121 * multiplier)
  )
  const totalTotals = buildPriceLine(
    PRICE_LINE_TYPE_TOTAL,
    buildEuroPrice(550 * multiplier),
    buildEuroPrice(48 * multiplier),
    buildEuroPrice(598 * multiplier)
  )
  priceSpecification.totalsPerPriceLineType = {
    [PRICE_LINE_TYPE_PRODUCT]: productTotals,
    [PRICE_LINE_TYPE_DISCOUNT]: discountTotals,
    [PRICE_LINE_TYPE_FEE]: feeTotals,
    [PRICE_LINE_TYPE_TOTAL]: totalTotals
  }

  return Promise.resolve(priceSpecification)
}

class MockOrderApiClient implements OrderApiClientInterface {
  getPriceSpecificationForOrder (order: Order): Promise<PriceSpecification> {
    return Promise.resolve(buildPriceSpecificationFromOrder(order))
  }

  getCombiDealDiscountSuggestionsForOrder (order: Order): Promise<CombiDealDiscountSuggestions> {
    return Promise.resolve({ 'combiDealDiscountSuggestions': [] })
  }

  getPaymentSpecificationForOrder (order: Order): Promise<PaymentSpecification> {
    return Promise.resolve(buildPaymentSpecificationFromOrder(order))
  }

  createOrder (order: Order): Promise<OrderCreated> {
    const orderCreated = buildOrderCreated()
    orderCreated.orderId = 'order-id'
    orderCreated.orderToken = 'order-token'

    return Promise.resolve(orderCreated)
  }

  async updateOrder (order: Order): Promise<ValidationResult> {
    const errors = []
    if (order.discountCode === 'invalidDiscount') {
      errors.push(
        {
          code: '60ebff78-e6dd-40b0-9e62-c14e523c10ad',
          propertyPath: 'order',
          message: 'Discount code not found'
        }
      )
    }
    if (order.discountCode === 'expiredDiscount') {
      errors.push(
        {
          code: '4ea531f8-67fc-4c93-b8c0-d6c49b055c58',
          propertyPath: 'order',
          message: 'Discount code is not applicable for this order'
        }
      )
    }
    if (order.discountCode === 'invalidDiscount') {
      errors.push(
        {
          code: '4ea531f8-67fc-4c93-b8c0-d6c49b055c58',
          propertyPath: 'order',
          message: 'Discount code is not applicable for this order'
        }
      )
    }

    if (errors.length > 0) {
      return { errors: errors }
    }
    return {}
  }

  initiatePayment (order: Order): Promise<OrderInitiatedPayment> {
    const orderInitiatedPayment = buildOrderInitiatedPayment()
    orderInitiatedPayment.paymentUrl = window.location.origin + '/nl/webshop/payment/status'

    return Promise.resolve(orderInitiatedPayment)
  }

  getOrderStatus (orderId: string, orderToken: string): Promise<OrderStatus> {
    const orderStatus = buildOrderStatus()
    orderStatus.status = ORDER_STATUS_PAID

    return Promise.resolve(orderStatus)
  }

  getPaymentMethodsOrder (order: Order): Promise<PaymentMethodsOrder> {
    const paymentMethodsOrder = buildPaymentMethodsOrder()
    paymentMethodsOrder.paymentMethods = [
      buildPaymentMethod('ideal', [
        buildPaymentOption('issuer', 'ABNAMRO'),
        buildPaymentOption('issuer', 'BUNQ'),
        buildPaymentOption('issuer', 'RABOBANK')
      ]),
      buildPaymentMethod('creditcard', [
        buildPaymentOption('issuer', 'MASTER'),
        buildPaymentOption('issuer', 'VISA')
      ]),
      buildPaymentMethod('sofort', [
        buildPaymentOption('issuer', 'SOFORT')
      ]),
      buildPaymentMethod('mistercash', [
        buildPaymentOption('issuer', 'MISTERCASH')
      ])
    ]
    paymentMethodsOrder.orderItemPaymentConfigurations = {}
    Object.keys(order.items).forEach(key => {
      paymentMethodsOrder.orderItemPaymentConfigurations[key] = buildOrderItemPaymentConfiguration([
        buildPaymentTermConfiguration('term', 1),
        buildPaymentTermConfiguration('term', 2),
        buildPaymentTermConfiguration('term', 4),
        buildPaymentTermConfiguration('term', 6)
      ])
    })

    return Promise.resolve(paymentMethodsOrder)
  }

  getAddressInformation (postalCode: string, houseNumber: string, houseNumberExtension: string): Promise<AddressInfo> {
    return Promise.resolve({
      address: {
        street: 'Palatijn',
        houseNumber: 3,
        houseNumber_addition: '',
        postal_code_number: '7521',
        postal_code_letters: 'PN',
        city: 'Enschede',
        community: 'Enschede',
        province: 'Overijssel',
        latitude: '52.23911384',
        longitude: '6.84475676',
        purpose: 'kantoorfunctie'
      }
    })
  }

  async validateOrder (order: Order, stage: OrderValidationStage): Promise<ValidationResult> {
    let payInTerms = false
    for (let id in order.items) {
      payInTerms = payInTerms || order.items[id].payInTerms
    }
    const errors = []
    if (order.customerDetails.firstName === '') {
      errors.push(
        {
          code: 'c1051bb4-d103-4f74-8988-acbcafc7fdc3',
          propertyPath: 'customerDetails.firstName',
          message: 'This value should not be blank'
        }
      )
    }
    if (!RegExp('^[A-z0-9\\s\\.]*$').test(String(order.customerDetails.surnamePrefix))) {
      errors.push(
        {
          code: 'de1e3db3-5ed4-4941-aae4-59f3667cc3a3',
          propertyPath: 'customerDetails.surnamePrefix',
          message: 'This value is not valid'
        }
      )
    }
    if (order.customerDetails.surname === '') {
      errors.push(
        {
          code: 'c1051bb4-d103-4f74-8988-acbcafc7fdc3',
          propertyPath: 'customerDetails.surname',
          message: 'This value should not be blank'
        }
      )
    }
    if (order.invoiceAddress.postalCode === '') {
      errors.push(
        {
          code: 'c1051bb4-d103-4f74-8988-acbcafc7fdc3',
          propertyPath: 'invoiceAddress.postalCode',
          message: 'This value should not be blank'
        }
      )
    }
    if (!RegExp('[0-9]{4}[A-Z]{2}').test(String(order.invoiceAddress.postalCode))) {
      errors.push(
        {
          code: 'de1e3db3-5ed4-4941-aae4-59f3667cc3a3',
          propertyPath: 'invoiceAddress.postalCode',
          message: 'This value is not valid'
        }
      )
    }
    if (payInTerms && !order.customerDetails.phoneNumber) {
      errors.push({
        code: 'c1051bb4-d103-4f74-8988-acbcafc7fdc3',
        propertyPath: 'customerDetails.phoneNumber',
        message: 'This value should not be blank'
      })
    }
    if (payInTerms && !order.customerDetails.bankAccountNumber) {
      errors.push({
        code: 'c1051bb4-d103-4f74-8988-acbcafc7fdc3',
        propertyPath: 'customerDetails.bankAccountNumber',
        message: 'This value should not be blank'
      })
    }

    if (errors.length > 0) {
      return { errors: errors }
    }
    return {}
  }

  async validateIban (iban: string): Promise<IbanValidationResult> {
    if (iban === 'NL 71 ABNA0 456431036') {
      return { formattedIban: 'NL71ABNA0456431036' }
    }
    return [
      {
        'code': '8988bd25-ddf0-40d2-ac6b-02fde4641ff6',
        'invalidValue': iban,
        'propertyPath': 'iban',
        'message': 'Invalid IBAN'
      }
    ]
  }

  async getSubscriptionByToken (token: string): Promise<Subscription> {
    return {
      subscriptionId: '13',
      engravedId: '3528023100165413',
      productTypeId: '4',
      productGroupId: '34',
      customerGroupId: '3',
      tariffTermId: '3',
      dateEffective: '2018-12-31',
      zoneId: null,
      starValue: null,
      fromStopId: '23',
      toStopId: '68',
      class: 'SecondClass'
    }
  }

  async cancelSubscriptionReminders (token: string): Promise<SubscriptionReminderCancellationResult> {
    return {}
  }

  async sendInvoice (orderId: string, orderToken: string, emailAddress: string): Promise<OrderSentResult> {
    return {}
  }

  async getInvoice (orderId: string, orderToken: string): Promise<string> {
    return ''
  }
}

export default MockOrderApiClient



// WEBPACK FOOTER //
// ./src/order-api/mock-order-api-client.js